import { type DriverOverviewRow, useDriverManagement } from '@/api/useDriverManagement'
import { usePayrollConfig } from '@/api/usePayrollConfig'
import type { components } from '@/types/api/v1'
import { PlusCircleOutlined } from '@ant-design/icons'
import type { UseQueryResult } from '@tanstack/react-query'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Button, Col, DatePicker, Modal, Row, Select, Table, notification } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

const { Option } = Select

type PayConfigDetail = components['schemas']['PayConfigDetail']
const formatDate = (date?: string | null) => {
  if (!date) return ''
  const year = dayjs(date).year()
  if (year < 1950 || year > 9000) return '--'

  return formatters.DATE(date)
}

const getSavePayload = (driver?: DriverOverviewRow | null, config?: PayConfigDetail) => {
  if (!driver || !config?.name || !config?.start_date) return
  return { driver_id: driver.id, name: config.name, date: config.start_date }
}

export const PayConfigModal = ({
  driver,
  showingModal,
  setShowingModal,
}: {
  driver?: DriverOverviewRow | null
  showingModal: boolean
  setShowingModal: (showing: boolean) => void
}) => {
  const { usePayrollConfigQuery } = usePayrollConfig()
  const { useDriverPayConfigUpdate } = useDriverManagement()
  const { data: payrollConfigs, isLoading: payrollConfigsLoading } = usePayrollConfigQuery() as UseQueryResult<
    { rows: { id: string; name: string }[] },
    Error
  >

  const [isEditing, setIsEditing] = useState(false)
  const [newConfig, setNewConfig] = useState<PayConfigDetail>()
  const { isPending, mutateAsync, data: updatedPayrollConfigs } = useDriverPayConfigUpdate(driver?.id)

  const driverConfigs = useMemo(() => {
    let configs =
      (updatedPayrollConfigs || driver?.pay_config_history)?.map((c, i, list) => ({
        ...c,
        key: c.name,
        isCurrent: i === list.length - 1,
      })) ?? []

    if (newConfig) {
      configs = [
        ...configs.map((c) => ({ ...c, isCurrent: false })),
        { ...newConfig, key: newConfig.name, isCurrent: true },
      ]
    }

    return configs
  }, [driver?.pay_config_history, newConfig, updatedPayrollConfigs])

  const onClose = useCallback(() => {
    setShowingModal(false)
    setNewConfig(undefined)
  }, [setShowingModal])

  const payload = useMemo(
    () => (isPending ? undefined : getSavePayload(driver, newConfig)),
    [driver, newConfig, isPending]
  )

  const [notificationApi, contextHolder] = notification.useNotification()

  function onCancel() {
    setIsEditing(false)
    setNewConfig(undefined)
  }

  function onSave() {
    if (!payload) return
    setNewConfig(undefined)
    setIsEditing(false)
    mutateAsync(
      { params: { query: payload } },
      {
        onError: (e) =>
          notificationApi.error({
            message: `Pay Configuration Update for ${driver?.name ?? 'Driver'} Failed`,
            description: e.detail?.[0]?.msg ?? 'An unknown error has occured',
          }),
      }
    )
  }

  const addNewConfig = useCallback(() => {
    setIsEditing(true)
    setNewConfig({
      name: undefined,
      start_date: dayjs().toISOString(),
    })
  }, [])

  return (
    <>
      {contextHolder}
      <Modal
        title='Driver Pay Configuration'
        visible={showingModal && !!driver}
        onCancel={onClose}
        width={700}
        footer={[
          <div key='footer' className='pay-config-footer'>
            <div className='label'>Currently Effective Pay Configuration</div>
            <div className='value'>
              <b>{driver?.pay_config_history?.at(-1)?.name ?? 'N/A'}</b>
            </div>
          </div>,
        ]}
      >
        <div className='driver-pay-config-name-date mt-3'>
          <div className='name'>
            <b>{driver?.name || ''}</b>
          </div>
          <div>
            <span className='pr-2'>Start Date:</span>
            <span>{formatDate(driver?.hire_date)}</span>
          </div>
        </div>
        <Row className='pb-3 pl-1 pay-schedule-label'>
          <Col span={24}>Pay Schedule</Col>
        </Row>
        <div style={{ margin: '0 -1em' }}>
          <Table
            loading={isPending}
            dataSource={driverConfigs}
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                render: (v, r) =>
                  isEditing && r.isCurrent ? (
                    <Select
                      loading={payrollConfigsLoading}
                      style={{ width: 180 }}
                      value={v}
                      onChange={(value) => setNewConfig({ ...newConfig, name: value })}
                    >
                      {payrollConfigs?.rows?.map(({ id, name }) => (
                        <Option key={id} value={name}>
                          {name}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    v
                  ),
              },
              {
                title: 'From Date',
                dataIndex: 'start_date',
                render: (v, r) =>
                  isEditing && r.isCurrent ? (
                    <DatePicker
                      placeholder='Start Date'
                      value={dayjs(v)}
                      onChange={(val) => setNewConfig({ ...newConfig, start_date: val.toISOString() })}
                    />
                  ) : (
                    <span>{formatDate(v)}</span>
                  ),
              },
              {
                title: 'To Date',
                dataIndex: 'end_date',
                render: (v, r) =>
                  r.isCurrent ? <span className='current'>Current</span> : <span>{formatDate(v)}</span>,
              },
            ]}
            pagination={false}
          />
        </div>
        <div className='add-driver-pay-config'>
          {isEditing ? (
            <>
              <Button onClick={onCancel}>Cancel</Button>
              <Button disabled={!payload} loading={isPending} className='ml-2 success' onClick={onSave}>
                Save
              </Button>
            </>
          ) : (
            <span className='add-new-config'>
              <PlusCircleOutlined className='mt-1' />
              <span onClick={addNewConfig} onKeyDown={addNewConfig} className='pl-2'>
                New Pay Config
              </span>
            </span>
          )}
        </div>
      </Modal>
    </>
  )
}
